import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import {
  AbcLoginComponentInfo,
  AbcProfileSelectionComponentInfo,
  ProfileSelectionGuardService,
} from "@barthauer/abc/core/authentication";
import { KAOSProfileSelectionComponent } from "@barthauer/kaos/core/authentication";
import { CompaniesListComponent } from "./core/companies/companies-list/companies-list.component";
import { CompaniesComponent } from "./core/companies/companies.component";
import { CoreComponent } from "./core/core.component";
import { GanttComponent } from "./core/customer-space/gantt/gantt.component";
import { MeasureDetailComponent } from "./core/customer-space/measure/measure-detail/measure-detail.component";
import { MeasureListComponent } from "./core/customer-space/measure/measure-list/measure-list.component";
import { MeasureTaskComponent } from "./core/customer-space/measure/measure-task/measure-task.component";
import { ReportsComponent } from "./core/customer-space/reports/reports.component";
import { RolesComponent } from "./core/customer-space/roles/roles.component";
import { StaffMembersComponent } from "./core/customer-space/staff-members/staff-members.component";
import { SubProjectDetailComponent } from "./core/customer-space/sub-project/sub-project-detail/sub-project-detail.component";
import { SubProjectListComponent } from "./core/customer-space/sub-project/sub-project-list/sub-project-list.component";
import { TrainingCenterComponent } from "./core/customer-space/training-center/training-center.component";
import { DashboardComponent } from "./core/dashboard/dashboard.component";
import { HomeComponent } from "./core/home/home.component";
import { LoginComponent } from "./core/login/login.component";
import { ActionType, ROLES } from "./core/models/enumeration-list.model";
import { RoleGuard } from "./core/services/guards/user-role.guard";
import { DocumentComponent } from "./core/shared/document/document.component";
import { UserRightComponent } from "./core/user-right/user-right.component";
import { ProfileSelectionGuard } from "./core/services/guards/profileSelectionGuard";
import { LandingPageComponent } from "./core/shared/landing-page/landing-page.component";
import { ProfileSelectionComponent } from "./core/shared/select-profil/select-profil.component";
import { ForgotPasswordComponent } from "./core/forgot-password/forgot-password.component";
import { NotificationListComponent } from "./core/customer-space/notification/notification-list/notification-list.component";

export class AIMSROUTES {
  public static readonly SELECT_PROFILE = "select-profile";
  public static readonly REGISTER = "register/:id";
  public static readonly RESET_PASSWORD = "reset-password/:id";
  public static readonly FORGOT_PASSWORD = "forgot-password";
  public static readonly ROOT = "";
  public static readonly COMPANIES = "companies";
  public static readonly PROJECT = "project";
  public static readonly COMPANIES_LIST = "companiesList";
  public static readonly DASHBOARD = "dashboard";
  public static readonly MEASURES = "measures";
  public static readonly MEASURE_DETAIL = "measureDetail/:from/:id";
  public static readonly DOCUMENTS = "documents";
  public static readonly TASKS = "tasks";
  public static readonly REPORTS = "reports";
  public static readonly GANTT = "gantt";
  public static readonly TRAINING_CENTER = "trainingCenter";
  public static readonly STAFF_MEMBERS = "staffMembers";
  public static readonly ROLES = "roles";
  public static readonly ROLE_RIGHTS = "roleRights";
  public static readonly SUBPROJECT = "subProject";
  public static readonly SUBPROJECT_DETAIL = "subProjectDetail/:id";
  public static readonly NOTIFICATION = "notification";
  public static readonly NOT_FOUND = "**";
}

const routes: Routes = [
  {
    path: AbcLoginComponentInfo.ROUTE_PATH,
    component: LoginComponent,
    data: {
      ApplicationTitle: "UBT Arena",
      LoginIntroHtml: "UBT Arena Login",
      ForwardTo: "select-profile",
    },
  },
  {
    path: AIMSROUTES.SELECT_PROFILE,
    component: ProfileSelectionComponent,
    data: {
      ApplicationTitle: "UBT select profile",
      ForceReloadOnApply: false,
      MarkerTag: "select-profile",
    },
  },
  {
    path: AIMSROUTES.REGISTER,
    component: LandingPageComponent,
  },
  {
    path: AIMSROUTES.RESET_PASSWORD,
    component: LandingPageComponent,
  },
  {
    path: AIMSROUTES.FORGOT_PASSWORD,
    component: ForgotPasswordComponent,
  },
  {
    path: AIMSROUTES.ROOT,
    component: CoreComponent,

    children: [
      {
        path: AIMSROUTES.COMPANIES,
        component: CompaniesComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],

        },
        loadChildren: () =>
          import("./core/shared/companies/companies.module").then(
            (module) => module.CompaniesModule,
          ),
      },
      {
        path: AIMSROUTES.PROJECT,
        component: HomeComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin, ROLES.CoreTeam, ROLES.SteeringCommittee],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.COMPANIES_LIST,
        component: CompaniesListComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin, ROLES.CoreTeam, ROLES.SteeringCommittee, ROLES.SUPER_ADMIN],
          permsision: [ActionType.SEE],
        },
      },

      {
        path: AIMSROUTES.DASHBOARD,
        component: DashboardComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },

      {
        path: AIMSROUTES.MEASURES,
        component: MeasureListComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.MEASURE_DETAIL,
        component: MeasureDetailComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.DOCUMENTS,
        component: DocumentComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin, ROLES.CoreTeam, ROLES.SteeringCommittee],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.TASKS,
        component: MeasureTaskComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.REPORTS,
        component: ReportsComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
        loadChildren: () =>
          import("./core/customer-space/reports/reports.module").then(
            (module) => module.ReportsModule,
          ),
      },
      {
        path: AIMSROUTES.GANTT,
        component: GanttComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.TRAINING_CENTER,
        component: TrainingCenterComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.MeasureManager,
            ROLES.MeasureManager,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
            ROLES.MeasureParticipant,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.STAFF_MEMBERS,
        component: StaffMembersComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin, ROLES.CoreTeam, ROLES.SteeringCommittee, ROLES.SUPER_ADMIN],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.ROLES,
        component: RolesComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin, ROLES.CoreTeam, ROLES.SteeringCommittee],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.ROLE_RIGHTS,
        component: UserRightComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [ROLES.Admin],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.SUBPROJECT,
        component: SubProjectListComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.SUBPROJECT_DETAIL,
        component: SubProjectDetailComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
        data: {
          roles: [
            ROLES.Admin,
            ROLES.CoreTeam,
            ROLES.SteeringCommittee,
            ROLES.SubprojectDeputy,
            ROLES.SubprojectResponsible,
          ],
          permsision: [ActionType.SEE],
        },
      },
      {
        path: AIMSROUTES.NOTIFICATION,
        component: NotificationListComponent,
        canActivate: [ProfileSelectionGuardService, RoleGuard],
      },

      {
        path: AIMSROUTES.NOT_FOUND,
        redirectTo: AIMSROUTES.COMPANIES,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [ProfileSelectionGuardService],
})
export class AppRoutingModule { }
